import DOMPurify from 'dompurify';
import { getHelpUrl } from '../utils';

const newWindowIcon = `<svg width="20" height="20" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M18.5 4.99987C18.5001 4.44758 18.9478 3.99993 19.5001 4L26.9991 4.001C27.5513 4.00107 27.9989 4.44869 27.999 5.00087L28 12.4999C28.0001 13.0522 27.5524 13.4999 27.0001 13.5C26.4478 13.5001 26.0001 13.0524 26 12.5001L25.9991 6.00087L19.4999 6C18.9476 5.99993 18.4999 5.55215 18.5 4.99987Z" fill="currentColor"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M27.7035 4.29646C28.094 4.68699 28.094 5.32015 27.7035 5.71068L18.7035 14.7107C18.313 15.1012 17.6798 15.1012 17.2893 14.7107C16.8988 14.3202 16.8988 13.687 17.2893 13.2965L26.2893 4.29646C26.6798 3.90594 27.313 3.90594 27.7035 4.29646Z" fill="currentColor"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M4.58579 8.58579C4.96086 8.21071 5.46957 8 6 8H14C14.5523 8 15 8.44772 15 9C15 9.55228 14.5523 10 14 10H6L6 26H22V18C22 17.4477 22.4477 17 23 17C23.5523 17 24 17.4477 24 18V26C24 26.5304 23.7893 27.0391 23.4142 27.4142C23.0391 27.7893 22.5304 28 22 28H6C5.46957 28 4.96086 27.7893 4.58579 27.4142C4.21071 27.0391 4 26.5304 4 26V10C4 9.46957 4.21071 8.96086 4.58579 8.58579Z" fill="currentColor"/>
</svg>`;

const parseSvgString = (svgString: string) => new DOMParser().parseFromString(svgString, 'image/svg+xml')
  .querySelector('svg');

const enum LinkTarget {
  BLANK = '_blank',
  SELF = '_self',
}

const BOOMI_HELP_TAG = 'boomi-help';
const BOOMI_HELP_ID_ATTRIBUTE = 'id';
const TAG_SEGMENT_NOWRAP = 'nowrap';
const NO_WRAP_STYLE = 'no-wrap';
const TARGET_ATTRIBUTE = 'target';
const REL_ATTRIBUTE = 'rel';
const REL_VALUES = 'noreferrer noopener';
const TEMPORARY_ATTRIBUTE = 'data-temp-href-target';
const ANCHOR_ELEMENT = 'a';
const HELP_LINK_STYLE = 'alert-help__link';

const makeAnchor = (href: string, content: string, offpage: boolean, hasNoWrap: boolean) => {
  const target = (offpage === true) ? LinkTarget.BLANK : LinkTarget.SELF;
  const anchorElement: HTMLAnchorElement = document.createElement(ANCHOR_ELEMENT);
  anchorElement.classList.add(HELP_LINK_STYLE);
  anchorElement.href = href;
  // Setting the Temp Target Attribute
  anchorElement.setAttribute(TEMPORARY_ATTRIBUTE, target);
  if (hasNoWrap) {
    anchorElement.classList.add(NO_WRAP_STYLE);
  }
  anchorElement.innerHTML = content;
  // Adding the new window icon svg over here
  const newWindowIconSvg = parseSvgString(newWindowIcon);
  if (target === LinkTarget.BLANK && newWindowIconSvg) {
    anchorElement.appendChild(newWindowIconSvg);
  }
  return anchorElement;
};

const interpretHelpTag = (node: Element) => {
  const helpId = node.getAttribute(BOOMI_HELP_ID_ATTRIBUTE) ?? '';
  const href = getHelpUrl(helpId) ?? '';
  const hasNoWrap = String(node.getAttribute(TAG_SEGMENT_NOWRAP) ?? '').toLowerCase() === 'true';
  const anchorElement = makeAnchor(href, node.innerHTML, true, hasNoWrap);
  node?.parentNode?.replaceChild(anchorElement, node);
};

DOMPurify.addHook('beforeSanitizeElements', (node: Node) => {
  if (node?.nodeName?.toLowerCase() === BOOMI_HELP_TAG
    && node instanceof Element && node.hasAttribute(BOOMI_HELP_ID_ATTRIBUTE)) {
    interpretHelpTag(node);
  }
});

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node?.tagName?.toLowerCase() === ANCHOR_ELEMENT && node.hasAttribute(TEMPORARY_ATTRIBUTE)) {
    node.setAttribute(TARGET_ATTRIBUTE, node.getAttribute(TEMPORARY_ATTRIBUTE) ?? LinkTarget.SELF);
    node.removeAttribute(TEMPORARY_ATTRIBUTE);
    if (node.getAttribute(TARGET_ATTRIBUTE) === LinkTarget.BLANK) {
      node.setAttribute(REL_ATTRIBUTE, REL_VALUES);
    }
  }
});

const sanitizeHtml = (htmlString: string) => DOMPurify.sanitize(htmlString);

export default sanitizeHtml;
